/**
 * @deprecated
 * IterationPreview - Individual iteration with quality scores and content preview
 * Shows iteration content, quality metrics, and selection controls
 */

import React, { useState, useEffect } from 'react';
import { 
  CheckCircle, 
  XCircle, 
  RotateCcw,
  Play,
  Image,
  FileText,
  Video,
  Eye,
  DollarSign,
  TrendingUp,
  Clock,
  Star
} from 'lucide-react';
import { StageType } from '@/types/execution';
import { API_BASE_URL, getApiHeaders } from '@/lib/api-base';

interface IterationPreviewProps {
  iteration: IterationData;
  stage: StageType;
  isSelected: boolean;
  compact?: boolean;
  onSelect: () => void;
  onView: () => void;
  onCompare?: () => void;
}

interface IterationData {
  id: string;
  iteration_number: number;
  content: any;
  scores: {
    actual: number;
    threshold: number;
    decision: 'accept' | 'improve' | 'reject';
  };
  cost: number;
  generated_at: Date;
  is_selected: boolean;
}

interface MediaUrls {
  preview_url?: string;
  full_url?: string;
  thumbnail_url?: string;
}

const IterationPreview: React.FC<IterationPreviewProps> = ({
  iteration,
  stage,
  isSelected,
  compact = false,
  onSelect,
  onView,
  onCompare
}) => {
  const [mediaUrls, setMediaUrls] = useState<MediaUrls>({});
  const [loading, setLoading] = useState(false);

  // Load media URLs from Supabase Storage
  useEffect(() => {
    if (iteration.content?.media_files) {
      loadMediaUrls();
    }
  }, [iteration.content]);

  const loadMediaUrls = async () => {
    setLoading(true);
    try {
      const urls: MediaUrls = {};

      // H-011: Use server API for signed URLs
      const getSignedUrl = async (path: string): Promise<string | null> => {
        try {
          const response = await fetch(`${API_BASE_URL}/api/storage/signed-url`, {
            method: 'POST',
            headers: getApiHeaders(true),
            credentials: 'include',
            body: JSON.stringify({ path })
          });
          if (response.ok) {
            const { url } = await response.json();
            return url;
          }
        } catch (err) {
          console.warn('Failed to get signed URL for:', path);
        }
        return null;
      };

      if (iteration.content.media_files?.preview_path) {
        const url = await getSignedUrl(iteration.content.media_files.preview_path);
        if (url) urls.preview_url = url;
      }

      if (iteration.content.media_files?.full_path) {
        const url = await getSignedUrl(iteration.content.media_files.full_path);
        if (url) urls.full_url = url;
      }

      if (iteration.content.media_files?.thumbnail_path) {
        const url = await getSignedUrl(iteration.content.media_files.thumbnail_path);
        if (url) urls.thumbnail_url = url;
      }

      setMediaUrls(urls);
    } catch (error) {
      console.error('Failed to load media URLs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDecisionColor = () => {
    switch (iteration.scores.decision) {
      case 'accept':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'improve':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'reject':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getDecisionIcon = () => {
    switch (iteration.scores.decision) {
      case 'accept':
        return <CheckCircle className="w-4 h-4" />;
      case 'improve':
        return <RotateCcw className="w-4 h-4" />;
      case 'reject':
        return <XCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getStageIcon = () => {
    switch (stage) {
      case 'script':
      case 'vision':
      case 'storyboard':
        return <FileText className="w-4 h-4" />;
      case 'frames':
        return <Image className="w-4 h-4" />;
      case 'videos':
        return <Video className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const renderContentPreview = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-20 bg-gray-100 rounded-lg">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-600"></div>
        </div>
      );
    }

    // Video preview
    if (stage === 'videos' && mediaUrls.preview_url) {
      return (
        <div className="relative">
          <video 
            src={mediaUrls.preview_url} 
            controls={!compact}
            className="w-full h-32 object-cover rounded-lg bg-gray-100"
            poster={mediaUrls.thumbnail_url}
          />
          {compact && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Play className="w-8 h-8 text-white bg-black bg-opacity-50 rounded-full p-2" />
            </div>
          )}
        </div>
      );
    }

    // Image preview (frames)
    if (stage === 'frames' && mediaUrls.preview_url) {
      return (
        <div className="relative">
          <img 
            src={mediaUrls.preview_url} 
            alt={`${stage} iteration ${iteration.iteration_number}`}
            className="w-full h-32 object-cover rounded-lg bg-gray-100"
          />
          {compact && (
            <div className="absolute top-2 right-2">
              <Image className="w-4 h-4 text-white bg-black bg-opacity-50 rounded p-1" />
            </div>
          )}
        </div>
      );
    }

    // Text-based content
    const textContent = iteration.content?.script_text || 
                       iteration.content?.description ||
                       iteration.content?.prompt ||
                       JSON.stringify(iteration.content).substring(0, 100);

    if (textContent) {
      return (
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="flex items-start space-x-2">
            {getStageIcon()}
            <p className={`text-gray-700 ${compact ? 'text-xs line-clamp-2' : 'text-sm line-clamp-3'}`}>
              {textContent.length > (compact ? 80 : 150) 
                ? textContent.substring(0, compact ? 80 : 150) + '...'
                : textContent
              }
            </p>
          </div>
        </div>
      );
    }

    // Fallback for other content types
    return (
      <div className="bg-gray-100 rounded-lg p-3 flex items-center justify-center">
        {getStageIcon()}
        <span className="ml-2 text-sm text-gray-600">
          {stage} content
        </span>
      </div>
    );
  };

  const renderScoreBreakdown = () => {
    if (compact) {
      return (
        <div className="flex items-center space-x-2">
          <div className={`flex items-center px-2 py-1 rounded text-xs font-medium border ${getDecisionColor()}`}>
            {getDecisionIcon()}
            <span className="ml-1">{iteration.scores.actual.toFixed(2)}</span>
          </div>
          <span className="text-xs text-gray-500">
            ${iteration.cost.toFixed(4)}
          </span>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div className="text-center">
          <TrendingUp className="w-4 h-4 mx-auto mb-1 text-gray-400" />
          <p className="font-medium text-gray-900">
            {iteration.scores.actual.toFixed(2)}
          </p>
          <p className="text-gray-500">Score</p>
        </div>
        <div className="text-center">
          <DollarSign className="w-4 h-4 mx-auto mb-1 text-gray-400" />
          <p className="font-medium text-gray-900">
            ${iteration.cost.toFixed(4)}
          </p>
          <p className="text-gray-500">Cost</p>
        </div>
        <div className="text-center">
          <Clock className="w-4 h-4 mx-auto mb-1 text-gray-400" />
          <p className="font-medium text-gray-900">
            {iteration.generated_at.toLocaleTimeString([], { 
              hour: '2-digit', 
              minute: '2-digit' 
            })}
          </p>
          <p className="text-gray-500">Generated</p>
        </div>
      </div>
    );
  };

  if (compact) {
    return (
      <div 
        className={`border rounded-lg p-3 cursor-pointer transition-all duration-200 hover:shadow-md ${
          isSelected 
            ? 'border-blue-500 bg-blue-50 shadow-md' 
            : 'border-gray-200 bg-white hover:border-gray-300'
        }`}
        onClick={onSelect}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-900">
              #{iteration.iteration_number}
            </span>
            {isSelected && (
              <Star className="w-4 h-4 text-blue-600 fill-current" />
            )}
          </div>
          
          <div className="flex items-center space-x-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onView();
              }}
              className="p-1 text-gray-400 hover:text-gray-600 rounded"
            >
              <Eye className="w-3 h-3" />
            </button>
          </div>
        </div>

        <div className="mb-2">
          {renderContentPreview()}
        </div>

        {renderScoreBreakdown()}
      </div>
    );
  }

  // Full view
  return (
    <div className={`border rounded-lg p-6 ${
      isSelected 
        ? 'border-blue-500 bg-blue-50' 
        : 'border-gray-200 bg-white'
    }`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <h4 className="text-lg font-semibold text-gray-900">
            Iteration {iteration.iteration_number}
          </h4>
          {isSelected && (
            <div className="flex items-center px-2 py-1 bg-blue-600 text-white text-xs rounded-full">
              <Star className="w-3 h-3 mr-1 fill-current" />
              Selected
            </div>
          )}
          <div className={`flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getDecisionColor()}`}>
            {getDecisionIcon()}
            <span className="ml-1 capitalize">{iteration.scores.decision}</span>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {onCompare && (
            <button
              onClick={onCompare}
              className="px-3 py-1 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Compare
            </button>
          )}
          {!isSelected && (
            <button
              onClick={onSelect}
              className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Select This
            </button>
          )}
        </div>
      </div>

      {/* Content Preview */}
      <div className="mb-4">
        {renderContentPreview()}
      </div>

      {/* Metrics */}
      <div className="bg-gray-50 rounded-lg p-4">
        {renderScoreBreakdown()}
      </div>

      {/* Quality Details */}
      {iteration.scores.actual > 0 && (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Quality Analysis</span>
            <span className={`text-sm font-medium ${
              iteration.scores.actual >= iteration.scores.threshold 
                ? 'text-green-600' 
                : 'text-yellow-600'
            }`}>
              {iteration.scores.actual >= iteration.scores.threshold ? 'Passed' : 'Below Threshold'}
            </span>
          </div>
          
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full ${
                iteration.scores.actual >= iteration.scores.threshold 
                  ? 'bg-green-500' 
                  : 'bg-yellow-500'
              }`}
              style={{ width: `${Math.min(iteration.scores.actual * 100, 100)}%` }}
            />
          </div>
          
          <div className="flex justify-between text-xs text-gray-600 mt-1">
            <span>0</span>
            <span>Threshold: {iteration.scores.threshold}</span>
            <span>1.0</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default IterationPreview;